export const services = [
  {
    id: 'web-development',
    icon: '🌐',
    title: 'Web Development',
    tagline: 'Fast, scalable, conversion-optimised.',
    color: '#f3e8ff',
    glowColor: '#9747FF',
    shortDesc: 'Next.js, React, headless CMS — we engineer for sub-2s load times and 90+ Lighthouse scores.',
    fullDesc: `Our web development team builds high-performance, SEO-ready web applications that don't just look good — they drive measurable business outcomes. We specialise in React, Next.js, and headless architectures that scale gracefully and rank well organically.

Every site we build targets sub-2 second load times, 90+ Lighthouse scores across all categories, and WCAG 2.1 accessibility compliance. We handle everything from design handoff to production deployment and ongoing performance monitoring.`,
    features: [
      'React / Next.js / TypeScript',
      'Headless CMS (Sanity, Contentful, Strapi)',
      'SEO-optimised architecture',
      'Sub-2s load times, 90+ Lighthouse',
      'WCAG 2.1 Accessibility',
      'CI/CD pipeline setup',
      'Performance monitoring & alerting',
      'E-commerce (Shopify, WooCommerce, custom)',
    ],
    process: ['Discovery & Architecture', 'Design System Setup', 'Sprint Development', 'QA & Performance', 'Launch & Monitoring'],
    timeline: '2–4 weeks (Depends on scope)',
    startingAt: '₹1,20,000 (Negotiable based on scope)',
    caseStudy: { metric: '+214%', label: 'organic traffic lift', client: 'Orbimart' },
  },
  {
    id: 'mobile-app',
    icon: '📱',
    title: 'Mobile App Development',
    tagline: 'Native feel. Unified codebase. Faster launch.',
    color: '#fce7f3',
    glowColor: '#DB2777',
    shortDesc: 'Cross-platform iOS & Android in React Native or Flutter. Native performance, single team.',
    fullDesc: `We build cross-platform mobile apps that feel native — because we use native APIs, follow platform-specific design conventions, and benchmark against device performance targets from day one.

Our React Native and Flutter apps cover everything from authentication flows and offline sync to real-time data and complex animations. We handle App Store and Play Store submissions, ongoing maintenance, and performance regression testing.`,
    features: [
      'React Native / Flutter',
      'iOS & Android from one codebase',
      'Biometric auth & push notifications',
      'Offline-first with sync',
      'Real-time data (WebSockets / Firebase)',
      'App Store & Play Store submissions',
      'OTA updates (CodePush / Expo)',
      'Analytics integration',
    ],
    process: ['Product Scoping', 'UX Prototyping', 'Native Development', 'Device QA Testing', 'Store Submission', 'Growth Support'],
    timeline: '6–12 weeks (Depends on scope)',
    startingAt: '₹1,80,000 (Negotiable based on scope)',
    caseStudy: { metric: '4.8★', label: 'App Store rating', client: 'PayFlow' },
  },
  {
    id: 'ai-solutions',
    icon: '🤖',
    title: 'AI Solutions',
    tagline: 'Intelligence woven into your workflow.',
    color: '#ccfbf1',
    glowColor: '#0AC5A8',
    shortDesc: 'Custom LLMs, chatbots, recommendation engines, and predictive analytics — built for your business.',
    fullDesc: `We design and build AI-powered systems that deliver measurable ROI — not demos. Our AI practice covers everything from GPT-4 integrations and custom fine-tuned models to computer vision pipelines and ML-powered recommendation engines.

Every AI system we build includes proper evaluation frameworks, fallback logic, observability dashboards, and human-in-the-loop escalation where needed. We don't ship models that can't be measured.`,
    features: [
      'LLM integration (GPT-4, Claude, Gemini)',
      'Custom fine-tuned models',
      'RAG (Retrieval-Augmented Generation)',
      'AI-powered chatbots & support',
      'Recommendation engines',
      'Predictive analytics pipelines',
      'Computer vision systems',
      'AI evaluation & monitoring',
    ],
    process: ['Use Case Validation', 'Data Audit', 'Model Selection', 'Integration Build', 'Evaluation & Safety', 'Production Deploy'],
    timeline: '8–16 weeks (Depends on scope)',
    startingAt: '₹2,50,000 (Negotiable based on scope)',
    caseStudy: { metric: '70%', label: 'support ticket deflection', client: 'Deskly SaaS' },
  },
  {
    id: 'digital-marketing',
    icon: '📈',
    title: 'Digital Marketing',
    tagline: 'Pipeline-focused. Revenue-measured.',
    color: '#fef9c3',
    glowColor: '#F7B731',
    shortDesc: 'AEO, SEO, paid media, and CRO that targets pipeline — not impressions. Measured by revenue.',
    fullDesc: `Our digital marketing practice is run by growth engineers, not traditional marketers. Every strategy we propose is tied to a revenue hypothesis, and every campaign is measured against pipeline and CAC — not vanity metrics like impressions or click-through rates.

We operate across SEO, Google/Meta paid media, email automation, conversion rate optimisation, and content strategy. We're not a "set it and forget it" agency — we embed with your team and iterate weekly based on data.`,
    features: [
      'Technical SEO & Core Web Vitals',
      'Google & Meta Ads management',
      'Email marketing automation',
      'Conversion rate optimisation (CRO)',
      'Content strategy & production',
      'Analytics & attribution setup',
      'Landing page design & testing',
      'Competitive intelligence',
    ],
    process: ['Audit & Baseline', 'Strategy & Hypotheses', 'Campaign Launch', 'Weekly Iteration', 'Reporting & Scale'],
    timeline: 'Ongoing (min. 3 months)',
    startingAt: '₹45,000/month (Negotiable based on scope)',
    caseStudy: { metric: '3×', label: 'pipeline in 90 days', client: 'LogiCo Freight' },
  },
];

export const portfolio = [
  {
    id: 'orbimart',
    thumb: '🏪',
    thumbBg: 'linear-gradient(135deg,#ede9fe,#dbeafe)',
    category: 'E-Commerce',
    title: 'Orbimart Retail Platform',
    client: 'Orbimart',
    service: 'web-development',
    summary: 'Legacy monolith → Next.js headless. Faster loads, stronger conversions.',
    description: `Orbimart came to us with a 7-year-old PHP monolith that was costing them customers — 4.8s load times, poor mobile UX, and an SEO footprint that had flatlined. We rebuilt the entire storefront in 8 weeks using Next.js, a Sanity headless CMS, and a redesigned checkout flow optimised for mobile conversion.

The result: load times dropped to 1.2s, Lighthouse performance went from 34 to 96, and Google rewarded the technical improvements with a sustained organic traffic surge.`,
    metrics: [
      { label: 'Organic Traffic', value: '+214%', color: '#9747FF' },
      { label: 'Conversion Rate', value: '+38%', color: '#0AC5A8' },
      { label: 'Lighthouse Score', value: '96/100', color: '#2563EB' },
      { label: 'Load Time', value: '1.2s', color: '#F7B731' },
    ],
    tech: ['Next.js 14', 'Sanity CMS', 'Vercel', 'Stripe', 'Algolia'],
    timeline: '8 weeks',
    year: '2024',
  },
  {
    id: 'deskly-ai',
    thumb: '🤖',
    thumbBg: 'linear-gradient(135deg,#ccfbf1,#dbeafe)',
    category: 'AI Integration',
    title: 'Deskly AI Support Bot',
    client: 'Deskly SaaS',
    service: 'ai-solutions',
    summary: 'GPT-4 customer support bot — deflected 70% of tier-1 tickets on day one.',
    description: `Deskly's support team was drowning in repetitive tier-1 tickets — password resets, billing questions, feature explanations. We built a GPT-4 powered support assistant with RAG over their knowledge base, Slack escalation for complex cases, and a full admin dashboard to monitor conversation quality.

The bot went live in 6 weeks and deflected 70% of incoming tickets from week one, reducing monthly support headcount cost by 52% while improving CSAT scores because users got instant answers.`,
    metrics: [
      { label: 'Ticket Deflection', value: '70%',  color: '#9747FF' },
      { label: 'Support Cost',      value: '−52%', color: '#0AC5A8' },
      { label: 'CSAT Score',        value: '4.7★', color: '#F7B731' },
      { label: 'Avg Response Time', value: '<2s',  color: '#2563EB' },
    ],
    tech: ['GPT-4 API', 'LangChain', 'Pinecone', 'Next.js', 'Slack API'],
    timeline: '6 weeks',
    year: '2024',
  },
  {
    id: 'payflow',
    thumb: '📱',
    thumbBg: 'linear-gradient(135deg,#fce7f3,#ede9fe)',
    category: 'Mobile App',
    title: 'PayFlow FinTech App',
    client: 'PayFlow',
    service: 'mobile-app',
    summary: 'React Native payments app — 4.8 stars, 50K downloads in month one.',
    description: `PayFlow needed a cross-platform mobile app for their payments startup — tight on time, tight on budget, high on ambition. We built a React Native app with biometric authentication, real-time transaction feeds via WebSocket, offline capability with background sync, and a slick onboarding flow that took conversion from 34% to 61%.

We handled the App Store and Play Store submission process, set up OTA update pipelines via CodePush, and embedded Mixpanel analytics for the product team.`,
    metrics: [
      { label: 'App Store Rating', value: '4.8★',  color: '#9747FF' },
      { label: 'Downloads (M1)',   value: '50K+',  color: '#0AC5A8' },
      { label: 'Onboarding Conv.', value: '61%',   color: '#2563EB' },
      { label: 'Crash Rate',       value: '<0.1%', color: '#F7B731' },
    ],
    tech: ['React Native', 'TypeScript', 'Firebase', 'CodePush', 'Mixpanel'],
    timeline: '12 weeks',
    year: '2023',
  },
  {
    id: 'logico',
    thumb: '📊',
    thumbBg: 'linear-gradient(135deg,#fef9c3,#fed7aa)',
    category: 'Digital Marketing',
    title: 'LogiCo B2B Lead Engine',
    client: 'LogiCo Freight',
    service: 'digital-marketing',
    summary: '3× pipeline growth in 90 days for a B2B logistics company.',
    description: `LogiCo had a great product but a broken go-to-market — scattered ad spend, no attribution, and a website that converted at 0.8%. We ran a 90-day blitz: rebuilt their landing pages with conversion-first design, restructured their Google Ads campaigns around buyer intent keywords, and built an outbound email sequence targeted at freight managers.

By day 90, inbound pipeline had tripled, cost-per-lead had dropped 44%, and the sales team had more qualified meetings than they could handle.`,
    metrics: [
      { label: 'Pipeline Growth', value: '3×',   color: '#9747FF' },
      { label: 'Cost per Lead',   value: '−44%', color: '#0AC5A8' },
      { label: 'Website Conv.',   value: '+180%', color: '#2563EB' },
      { label: 'ROI (90 days)',   value: '6.2×', color: '#F7B731' },
    ],
    tech: ['Google Ads', 'Meta Ads', 'HubSpot', 'Unbounce', 'GA4'],
    timeline: '90 days',
    year: '2024',
  },
  {
    id: 'medivault',
    thumb: '🏥',
    thumbBg: 'linear-gradient(135deg,#e0f2fe,#ccfbf1)',
    category: 'Healthcare SaaS',
    title: 'MediVault Patient Portal',
    client: 'MediVault',
    service: 'web-development',
    summary: 'HIPAA-compliant scheduling + telemedicine. Zero downtime since launch.',
    description: `MediVault needed a HIPAA-compliant patient portal to replace their paper-based scheduling and introduce telemedicine. The brief: fully accessible, mobile-first, integrated with their existing EHR system via HL7 FHIR APIs, and ready in 14 weeks.

We delivered on time with end-to-end encryption, audit logging, role-based access control, and a telemedicine module built on Daily.co. The platform has run at 99.9% uptime since launch and bookings are up 180%.`,
    metrics: [
      { label: 'Booking Increase', value: '+180%', color: '#9747FF' },
      { label: 'Uptime',           value: '99.9%', color: '#0AC5A8' },
      { label: 'HIPAA Compliant',  value: '✓',     color: '#2563EB' },
      { label: 'Launch Time',      value: '14 wks', color: '#F7B731' },
    ],
    tech: ['React', 'Node.js', 'PostgreSQL', 'Daily.co', 'AWS'],
    timeline: '14 weeks',
    year: '2023',
  },
  {
    id: 'learnhub',
    thumb: '🎓',
    thumbBg: 'linear-gradient(135deg,#ede9fe,#fce7f3)',
    category: 'EdTech',
    title: 'LearnHub LMS',
    client: 'LearnHub',
    service: 'ai-solutions',
    summary: 'AI-powered LMS with adaptive quizzing. 91% completion rate, 25K learners.',
    description: `LearnHub wanted an LMS that felt like Netflix — personalised, progressive, and addictive. We built a full custom LMS with AI-powered content recommendations (collaborative filtering + LLM-based content tagging), an adaptive quiz engine that adjusts difficulty based on learner performance, and a cohort-based social learning layer.

The platform scaled from 2,000 to 25,000 active learners in 8 months, with a 91% course completion rate — versus an industry average of 13%.`,
    metrics: [
      { label: 'Completion Rate', value: '91%',  color: '#9747FF' },
      { label: 'Active Users',    value: '25K+', color: '#0AC5A8' },
      { label: 'Learner NPS',     value: '72',   color: '#2563EB' },
      { label: 'Courses Created', value: '340+', color: '#F7B731' },
    ],
    tech: ['React', 'Python', 'FastAPI', 'OpenAI', 'PostgreSQL', 'Redis'],
    timeline: '16 weeks',
    year: '2024',
  },
];

export const testimonials = [
  { id: 1, stars: 5, quote: '"NextGenHub rebuilt our platform in 8 weeks flat. Traffic doubled, conversion went up 38%. They think in revenue, not features."', name: 'Ravi Kumar',  role: 'CEO, Orbimart',    avatar: 'RK', bg: 'linear-gradient(135deg,#9747FF,#E040FB)' },
  { id: 2, stars: 5, quote: '"The AI bot they built handles 70% of our support load. Positive ROI in 6 weeks. I wish we\'d hired them a year earlier."',     name: 'Shreya Patel', role: 'CTO, Deskly SaaS',  avatar: 'SP', bg: 'linear-gradient(135deg,#0AC5A8,#2563EB)' },
  { id: 3, stars: 5, quote: '"Discovery to App Store in 14 weeks. 4.8 stars, 50K downloads in month one. The best technical bet we\'ve made."',             name: 'Arjun Mehta',  role: 'Founder, PayFlow',  avatar: 'AM', bg: 'linear-gradient(135deg,#7C3AED,#0AC5A8)' },
  { id: 4, stars: 5, quote: '"Pipeline tripled in 90 days. The team integrated so deeply with ours that it felt like an in-house growth team."',             name: 'Priya Nair',   role: 'VP Marketing, LogiCo', avatar: 'PN', bg: 'linear-gradient(135deg,#DB2777,#9747FF)' },
];

export const blogPosts = [
  {
    id: 'llm-latency-60',
    thumb: '🧠',
    thumbBg: 'linear-gradient(135deg,#ede9fe,#dbeafe)',
    category: 'AI Engineering',
    title: 'How we cut LLM latency by 60% with streaming and edge caching',
    summary: 'A technical breakdown of the architecture choices behind our fastest AI product to date.',
    readTime: '8 min',
    date: 'March 2025',
    content: `When Deskly asked us to build their AI support assistant, one of our non-negotiables was that the bot had to feel fast — humans abandon chatbots that take more than 3 seconds to respond.

## The baseline problem

Our first implementation used a standard request-response pattern: user sends message → we call the GPT-4 API → we wait for the full response → we display it. Time to first token: ~2.8 seconds. Total response time: 4–6 seconds. Unacceptable.

## Solution 1: Server-Sent Events (SSE) for streaming

The single biggest win. Instead of waiting for the full completion, we stream tokens as they arrive from the API and push them to the frontend via SSE. Time to first token dropped to ~600ms — the bot appears to "think and type" in real time, which dramatically improves perceived performance even if total response time is similar.

## Solution 2: Semantic response caching with Pinecone

For a support bot, many questions are semantically similar even if worded differently. "How do I reset my password?" and "Forgot my password, what do I do?" should return the same answer.

We cache embeddings of previous Q&A pairs in Pinecone. When a new message comes in, we compute its embedding and check if any cached pair has cosine similarity > 0.92. If yes, we stream the cached answer directly — zero LLM call. Cache hit rate stabilised at around 34% of all queries.

## Solution 3: Edge deployment via Cloudflare Workers

Moving the inference proxy from a Node.js server in us-east-1 to Cloudflare Workers reduced network latency for Indian and Southeast Asian users by ~180ms just from geography.

## Result

Combined, these three changes took median response latency from 4.2 seconds to 1.7 seconds — a 60% reduction — while reducing API costs by ~29% due to cache hits.`,
  },
  {
    id: 'lighthouse-98',
    thumb: '⚡',
    thumbBg: 'linear-gradient(135deg,#ccfbf1,#dbeafe)',
    category: 'Performance',
    title: 'Achieving 98 Lighthouse scores at scale: our complete Next.js checklist',
    summary: 'Every optimisation technique we run on every client project — compiled into one reference guide.',
    readTime: '6 min',
    date: 'February 2025',
    content: `After shipping 40+ Next.js projects, we've codified the exact set of optimisations that reliably get us to 90+ Lighthouse scores — often 95–98 — in production.

## Images (the biggest lever)

- Use \`next/image\` for all images. It handles WebP/AVIF conversion, responsive srcsets, and lazy loading automatically.
- Set explicit \`width\` and \`height\` on every image to prevent Cumulative Layout Shift (CLS).
- Use \`priority\` prop on the LCP image only — don't overuse it.

## Fonts

- Load Google Fonts via \`next/font\` — it downloads fonts at build time and self-hosts them, eliminating the Google Fonts render-blocking request.
- Set \`display: 'swap'\` and preload only the weights you actually use.

## JavaScript bundle

- Audit your bundle with \`@next/bundle-analyzer\`. Anything over 50kB gzipped deserves scrutiny.
- Use dynamic imports (\`next/dynamic\`) for components not needed on initial load (modals, charts, maps).
- Avoid barrel files (\`index.js\` that re-exports everything) — they prevent tree-shaking.

## Core Web Vitals specifics

- **LCP**: Ensure the hero image is priority-loaded and served from a CDN with appropriate cache headers.
- **CLS**: Reserve space for all async-loaded content (images, ads, embeds) before they load.
- **INP**: Move expensive computation to web workers or debounce input handlers.

## Server-side optimisation

- Use static generation (\`generateStaticParams\`) for all pages where data doesn't change per-request.
- Use ISR (Incremental Static Regeneration) with appropriate \`revalidate\` times for semi-dynamic pages.
- Enable Vercel Edge Network caching with proper \`Cache-Control\` headers.

Running this checklist consistently gets us to 95+ Performance, 100 SEO, 95+ Accessibility, and 100 Best Practices on virtually every project.`,
  },
  {
    id: 'b2b-pipeline-3x',
    thumb: '📈',
    thumbBg: 'linear-gradient(135deg,#fef9c3,#fed7aa)',
    category: 'Growth',
    title: 'The B2B lead gen playbook that 3×\'d pipeline in 90 days',
    summary: 'Exact strategy, ad copy angles, and landing page structure from a real campaign.',
    readTime: '10 min',
    date: 'January 2025',
    content: `When LogiCo came to us, their pipeline was stagnant. Decent product, good pricing, no distribution. We had 90 days to fix it. Here's the exact playbook.

## Week 1–2: Audit everything

Before spending a rupee on ads, we audited the full funnel: Google Analytics data, ad account history, CRM deal stages, sales call recordings. Key findings:
- Website converting at 0.8% (industry benchmark: 2–3%)
- Ad campaigns targeting broad keywords with no intent signal
- No email follow-up sequence after form fills
- Sales cycle data showed "freight manager" title closed 2× faster than others

## Week 3–4: Fix the foundation

We rebuilt the landing page with one job: convert freight managers. Single headline ("Cut freight costs by 20% in 60 days"), three proof points (client logos, metric cards), one CTA. Conversion went from 0.8% to 2.4% before we'd changed a single ad.

## Week 5–8: Rebuild paid campaigns

We restructured the Google Ads account around three intent buckets:
1. **High intent**: "freight management software", "logistics TMS pricing" → direct demo CTA
2. **Mid intent**: "reduce freight costs", "freight tracking software" → ROI calculator landing page
3. **Awareness**: competitor terms → comparison landing page

CPL dropped 44% in the first full month because we stopped paying for clicks that would never convert.

## Week 9–12: Email sequences and retargeting

We built a 6-email sequence for new leads triggered on form fill:
1. Immediate: "Here's what happens next" (sets expectations, builds trust)
2. Day 2: ROI calculator tool
3. Day 5: Case study (LogiCo competitor success story)
4. Day 9: Objection handling ("Why not spreadsheets?")
5. Day 14: Social proof video
6. Day 21: Direct ask for a 15-minute call

Open rate: 48%. Response rate: 12%. The sequence alone generated 23 qualified meetings in the first month.

## The result

By day 90: 3× pipeline volume, 44% lower CPL, 6.2× measured ROI on our retainer. The sales team asked us to slow down new leads because they were at capacity.`,
  },
];

export const stats = [
  { num: '200+', label: 'Projects delivered', color: 'grad-text' },
  { num: '98%',  label: 'Client satisfaction', color: 'teal-text' },
  { num: '3.2×', label: 'Average ROI', color: 'grad-text' },
  { num: '7 yrs', label: 'Industry experience', color: '' },
];

export const whyUs = [
  { icon: '⚡', title: 'Fast Delivery',        bg: '#f3e8ff', desc: 'Agile sprints, weekly demos, transparent timelines — and we actually ship on time.' },
  { icon: '📐', title: 'Scalable Architecture', bg: '#ccfbf1', desc: 'Cloud-native foundations engineered to handle 10× traffic without rearchitecting.' },
  { icon: '💰', title: 'ROI-Driven',            bg: '#fef9c3', desc: 'Every decision is weighed by revenue impact — we skip features that don\'t move the needle.' },
  { icon: '🧠', title: 'Senior-Only Team',      bg: '#fce7f3', desc: 'No juniors on your project. Engineers with 5–12 years of domain expertise, every engagement.' },
];

export const processSteps = [
  { num: '1', title: 'Discovery',   desc: 'Goals, users, landscape — no assumptions, only evidence.' },
  { num: '2', title: 'Design',      desc: 'Wireframes and prototypes before a single line of code.' },
  { num: '3', title: 'Build',       desc: 'Agile sprints, weekly demos, full code transparency.' },
  { num: '4', title: 'Launch',      desc: 'Zero-downtime deploy + 30 days of included support.' },
  { num: '5', title: 'Grow',        desc: 'Analytics, iteration, and a roadmap that never plateaus.' },
];
